import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fehlerText } from "@/lib/fehler";
import {
  Loader2, Plus, Trash2, Save, ChevronLeft, Link2, Mail, FileText, MessageCircle, Sparkles,
  Check, X, Clock, Receipt,
} from "lucide-react";

import RechnungNachEinsatz from "./RechnungNachEinsatz";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm";
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";
const EUR = (v) => `${(Number(v) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const STATUS = {
  offen: { label: "Offen", cls: "text-[#BA6D52] border-[#E8CFC4] bg-[#FAF5F1]", icon: Clock },
  rueckmeldung: { label: "Änderungswunsch", cls: "text-[#8A5A1E] border-[#E6D2A8] bg-[#FBF5E8]", icon: MessageCircle },
  angenommen: { label: "Angenommen", cls: "text-[#4F6B3E] border-[#CFE0C3] bg-[#F3F7EF]", icon: Check },
  abgelehnt: { label: "Abgelehnt", cls: "text-[#A6543F] border-[#E8CFC8] bg-[#FBF1EF]", icon: X },
};

const FELDER_BASIS = [
  ["firma", "Firma / Kunde", "md:col-span-2"],
  ["ansprechpartner", "Ansprechpartner", ""],
  ["email", "E-Mail", ""],
  ["telefon", "Telefon (für WhatsApp)", ""],
];

const FELDER_MENUE = [
  ["anlass", "Anlass", ""],
  ["datum", "Datum", "", "date"],
  ["uhrzeit", "Uhrzeit", "", "time"],
  ["personen", "Personen", "", "number"],
  ["ort", "Ort / Adresse", "md:col-span-2"],
  ["preis_pro_person_netto", "Preis pro Person (netto €)", "", "number"],
  ["anfahrt_pauschale_netto", "Fahrtkosten netto (€)", "", "number"],
  ["anfahrt_km", "Entfernung ca. (km)", "", "number"],
  ["gueltig_bis", "Angebot gültig bis", "", "date"],
  ["slug", "Adresse der Seite (/angebot-fuer/…)", "md:col-span-2"],
  ["anrede", "Anrede — wird zu „Guten Tag Herr Stutz,“", ""],
  ["untertitel", "Untertitel auf der Menükarte", ""],
];

const FELDER_TAGESSATZ = [
  ["anlass", "Einsatz / Bezeichnung (optional)", ""],
  ["uhrzeit", "Beginn (optional)", "", "time"],
  ["ort", "Betrieb / Adresse", "md:col-span-2"],
  ["tagessatz_netto", "Tagessatz netto (€)", "", "number"],
  ["stunden_pro_tag", "Enthaltene Stunden pro Tag", "", "number"],
  ["anfahrt_pauschale_netto", "Anfahrtspauschale netto (€)", "", "number"],
  ["anfahrt_km", "Entfernung ca. (km)", "", "number"],
  ["gueltig_bis", "Angebot gültig bis", "", "date"],
  ["slug", "Adresse der Seite (/angebot-fuer/…)", "md:col-span-2"],
  ["anrede", "Anrede — wird zu „Guten Tag Herr Stutz,“", ""],
];

const FELDER_STUNDEN = [
  ["anlass", "Einsatz / Bezeichnung (optional)", ""],
  ["uhrzeit", "Beginn (optional)", "", "time"],
  ["ort", "Betrieb / Adresse", "md:col-span-2"],
  ["stundensatz_netto", "Stundensatz netto (€)", "", "number"],
  ["anfahrt_pauschale_netto", "Anfahrtspauschale netto (€)", "", "number"],
  ["anfahrt_km", "Entfernung ca. (km)", "", "number"],
  ["gueltig_bis", "Angebot gültig bis", "", "date"],
  ["slug", "Adresse der Seite (/angebot-fuer/…)", "md:col-span-2"],
  ["anrede", "Anrede — wird zu „Guten Tag Herr Stutz,“", ""],
];

const EINSATZTAGE = (a) => {
  const art = a.termin_art || "tag";
  if (art === "tage") {
    const alle = new Set([...(a.tage || []).filter(Boolean), ...(a.datum ? [a.datum] : [])]);
    return alle.size;
  }
  if (art === "zeitraum" && a.datum && a.datum_bis) {
    const tage = Math.round((new Date(a.datum_bis) - new Date(a.datum)) / 86400000) + 1;
    return tage > 0 && tage <= 61 ? tage : 1;
  }
  return a.datum ? 1 : 0;
};

// Spiegelt _summen() im Backend — damit die Vorschau sofort mitrechnet
const rechneSummen = (a) => {
  const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
  const satz = (a.kundentyp || "firma") === "privat" ? 7 : 19;
  const anfahrt = r2(a.anfahrt_pauschale_netto);
  const art = a.art || "menue";
  if (art === "tagessatz") {
    const tagessatz = r2(a.tagessatz_netto);
    const tage = Math.max(1, EINSATZTAGE(a));
    const netto = r2(tagessatz * tage + anfahrt);
    const mwst = r2(netto * satz / 100);
    return { art, tagessatz_netto: tagessatz, tagessatz_brutto: r2(tagessatz * (1 + satz / 100)),
      tage, stunden_pro_tag: r2(a.stunden_pro_tag), tage_netto: r2(tagessatz * tage),
      anfahrt_netto: anfahrt, anfahrt_km: r2(a.anfahrt_km), personen: 0,
      netto, mwst_prozent: satz, mwst_betrag: mwst, brutto: r2(netto + mwst) };
  }
  if (art === "stunden") {
    const stundensatz = r2(a.stundensatz_netto);
    return { art, stundensatz_netto: stundensatz, stundensatz_brutto: r2(stundensatz * (1 + satz / 100)),
      anfahrt_netto: anfahrt, anfahrt_brutto: r2(anfahrt * (1 + satz / 100)), anfahrt_km: r2(a.anfahrt_km),
      personen: 0, netto: anfahrt, mwst_prozent: satz, mwst_betrag: r2(anfahrt * satz / 100),
      brutto: r2(anfahrt * (1 + satz / 100)) };
  }
  const personen = Math.max(0, parseInt(a.personen, 10) || 0);
  const pp = r2(a.preis_pro_person_netto);
  const positionen = r2(pp * personen);
  const netto = r2(positionen + anfahrt);
  const mwst = r2(netto * satz / 100);
  return { art, personen, pro_person_netto: pp, pro_person_brutto: r2(pp * (1 + satz / 100)),
    positionen_netto: positionen, positionen_brutto: r2(positionen * (1 + satz / 100)),
    anfahrt_netto: anfahrt, anfahrt_brutto: r2(anfahrt * (1 + satz / 100)), anfahrt_km: r2(a.anfahrt_km),
    netto, mwst_prozent: satz, mwst_betrag: mwst, brutto: r2(netto + mwst) };
};

export default function AngebotSeitenTab({ pwd }) {
  const headers = { "X-Admin-Password": pwd };
  const [liste, setListe] = useState([]);
  const [kalkulationen, setKalkulationen] = useState([]);
  const [aktiv, setAktiv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [neuTyp, setNeuTyp] = useState("firma");
  const [menuekarten, setMenuekarten] = useState([]);
  const [vorlagen, setVorlagen] = useState({ vorlagen: [], zeilen: [] });

  const load = useCallback(async () => {
    try {
      const [a, k, m, v] = await Promise.all([
        axios.get(`${API}/admin/angebotsseiten`, { headers }),
        axios.get(`${API}/admin/kalkulation/kalkulationen`, { headers }),
        axios.get(`${API}/admin/menuekarten`, { headers }),
        axios.get(`${API}/admin/angebotsseiten/leistungen-vorlagen`, { headers }),
      ]);
      setListe(a.data || []);
      setKalkulationen(k.data || []);
      setMenuekarten(m.data || []);
      setVorlagen(v.data || { vorlagen: [], zeilen: [] });
    } catch { toast.error("Angebotsseiten konnten nicht geladen werden"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const ausKalkulation = async (kalkId) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/angebotsseiten/aus-kalkulation/${kalkId}?kundentyp=${neuTyp}`, {}, { headers });
      await load();
      setAktiv(data);
      toast.success("Angebotsseite erstellt — jetzt Texte prüfen");
    } catch (err) { toast.error(fehlerText(err, "Erstellen fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const leer = async (art = "menue") => {
    setBusy(true);
    try {
      const basis = art === "stunden"
        ? { firma: "Neuer Betrieb", anlass: "Mietkoch-Einsatz", kundentyp: "firma", art: "stunden",
            termin_art: "tag", stundensatz_netto: 42 }
        : art === "tagessatz"
        ? { firma: "Neuer Betrieb", anlass: "Mietkoch-Einsatz", kundentyp: "firma", art: "tagessatz",
            termin_art: "tage", tagessatz_netto: 420, stunden_pro_tag: 10 }
        : { firma: "Neuer Kunde", anlass: "Dinner", personen: 10, kundentyp: neuTyp, art: "menue" };
      const { data } = await axios.post(`${API}/admin/angebotsseiten`, basis, { headers });
      await load();
      setAktiv(data);
    } catch (err) { toast.error(fehlerText(err, "Anlegen fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const speichern = async () => {
    setBusy(true);
    try {
      const zahl = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
      const { data } = await axios.put(`${API}/admin/angebotsseiten/${aktiv.id}`, {
        ...aktiv,
        personen: parseInt(aktiv.personen, 10) || 0,
        preis_pro_person_netto: zahl(aktiv.preis_pro_person_netto),
        stundensatz_netto: zahl(aktiv.stundensatz_netto),
        tagessatz_netto: zahl(aktiv.tagessatz_netto),
        stunden_pro_tag: zahl(aktiv.stunden_pro_tag),
        anfahrt_pauschale_netto: zahl(aktiv.anfahrt_pauschale_netto),
        anfahrt_km: zahl(aktiv.anfahrt_km),
      }, { headers });
      setAktiv(data);
      await load();
      toast.success("Gespeichert");
    } catch (err) { toast.error(fehlerText(err, "Speichern fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const loeschen = async (a) => {
    if (!window.confirm(`Angebotsseite „${a.firma || a.nummer}" löschen?`)) return;
    await axios.delete(`${API}/admin/angebotsseiten/${a.id}`, { headers });
    setAktiv(null);
    await load();
  };

  const mailen = async (a) => {
    const to = a.email || window.prompt("E-Mail-Adresse des Kunden:") || "";
    if (!to) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/angebotsseiten/${a.id}/send-email`, { email: to }, { headers });
      await load();
      if (aktiv?.id === a.id) setAktiv(data);
      toast.success(a.status === "rueckmeldung"
        ? "Überarbeitetes Angebot verschickt — Status wieder offen"
        : "Angebot per E-Mail verschickt");
    } catch (err) { toast.error(fehlerText(err, "Versand fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const linkKopieren = async (a) => {
    try {
      await navigator.clipboard.writeText(a.url);
      toast.success("Link kopiert");
    } catch { window.prompt("Link kopieren:", a.url); }
  };

  const setFeld = (patch) => setAktiv((a) => ({ ...a, ...patch }));
  const setGang = (i, patch) => setAktiv((a) => ({
    ...a, gaenge: a.gaenge.map((g, x) => (x === i ? { ...g, ...patch } : g)),
  }));

  // ---------------- Übersicht
  if (!aktiv) {
    return (
      <div className="space-y-6" data-testid="angebotsseiten-tab">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-sm text-[#757067] max-w-xl">
            Aus einer Kalkulation wird eine eigene Kundenseite im Stil der Eventseite — mit Menü,
            Leistungen, Preis pro Person und Gesamtsumme. Der Kunde nimmt direkt dort an, der
            Termin wird dann im Kalender blockiert. Interne Zahlen sieht er nie.
            Für Mietkoch-Einsätze gibt es das Stundensatz-Angebot: Der Betrieb bestätigt nur deinen
            Stundensatz für die Einsatztage — abgerechnet wird später über das Stundenkonto.
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => leer("menue")} disabled={busy} data-testid="angebotsseite-neu"
              className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Menü-Angebot
            </button>
            <button onClick={() => leer("stunden")} disabled={busy} data-testid="angebotsseite-neu-stunden"
              className="border border-[#E3DECE] text-[#757067] hover:text-[#BA6D52] hover:bg-[#FAF5F1] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Stundensatz-Angebot
            </button>
            <button onClick={() => leer("tagessatz")} disabled={busy} data-testid="angebotsseite-neu-tagessatz"
              className="border border-[#E3DECE] text-[#757067] hover:text-[#BA6D52] hover:bg-[#FAF5F1] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Tagessatz-Angebot
            </button>
          </div>
        </div>

        <div className="border border-[#E3DECE] bg-[#FAFAF7] p-4" data-testid="angebotsseite-aus-kalk">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-2">Kundentyp</p>
          <div className="flex gap-2">
            {[["firma", "Firma · 19 % MwSt"], ["privat", "Privatkunde · 7 % MwSt"]].map(([wert, label]) => (
              <button key={wert} onClick={() => setNeuTyp(wert)} data-testid={`angebotsseite-neutyp-${wert}`}
                className={`text-[11px] px-3 py-2 border ${neuTyp === wert
                  ? "border-[#BA6D52] text-[#BA6D52] bg-white"
                  : "border-[#E3DECE] text-[#757067] bg-white hover:bg-[#FAF5F1]"}`}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#9A958C] mt-2">
            Privatkunden: 7 % auf Speisen (§ 12 Abs. 2 UStG) — wird automatisch gesetzt.
          </p>

          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mt-5 mb-2">
            Aus Kalkulation erzeugen
          </p>
          {kalkulationen.length === 0 ? (
            <p className="text-sm text-[#757067]">
              Noch keine Kalkulation gespeichert — erst unter „Kalkulation" rechnen und speichern.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 mt-2">
              {kalkulationen.map((k) => (
                <button key={k.id} onClick={() => ausKalkulation(k.id)} disabled={busy}
                  data-testid={`angebotsseite-kalk-${k.id}`}
                  className="text-[11px] px-3 py-2 border border-[#E3DECE] bg-white text-[#575249] hover:border-[#BA6D52] hover:bg-[#FAF5F1] inline-flex items-center gap-2">
                  <Sparkles size={12} className="text-[#BA6D52]" />
                  {k.name}{k.kunde ? ` · ${k.kunde}` : ""}{k.personen ? ` · ${k.personen} P.` : ""}
                </button>
              ))}
            </div>
          )}
        </div>

        {liste.length === 0 ? (
          <p className="text-sm text-[#757067]" data-testid="angebotsseiten-leer">
            Noch keine Angebotsseite erstellt.
          </p>
        ) : (
          <div className="space-y-2">
            {liste.map((a) => {
              const st = STATUS[a.status] || STATUS.offen;
              const Icon = st.icon;
              return (
                <div key={a.id} className="border border-[#E3DECE] bg-white px-4 py-4"
                  data-testid={`angebotsseite-${a.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <button onClick={() => setAktiv(a)} className="text-left flex-1 min-w-[220px]"
                      data-testid={`angebotsseite-open-${a.id}`}>
                      <p className="font-serif text-lg text-[#2C2A26]">
                        {a.firma || a.ansprechpartner || "Angebot"}
                      </p>
                      <p className="text-xs text-[#757067] mt-1">
                        {a.nummer}{a.anlass ? ` · ${a.anlass}` : ""}
                        {a.datum ? ` · ${a.datum.split("-").reverse().join(".")}` : ""}
                        {a.summen?.personen ? ` · ${a.summen.personen} Personen` : ""}
                        {a.summen?.art === "tagessatz"
                          ? ` · ${a.summen.tage} Einsatztag${a.summen.tage !== 1 ? "e" : ""}` : ""}
                      </p>
                      <p className="text-[11px] uppercase tracking-[0.15em] text-[#BA6D52] mt-2">
                        {a.summen?.art === "tagessatz"
                          ? `${EUR(a.summen?.tagessatz_netto)} / Tag · ${EUR(a.summen?.netto)} netto`
                          : a.summen?.art === "stunden"
                          ? `${EUR(a.summen?.stundensatz_netto)} / Std. netto`
                          : `${EUR(a.summen?.pro_person_netto)} p. P. · ${EUR(a.summen?.netto)} netto`}
                        {a.abgelaufen && a.status === "offen" ? " · abgelaufen" : ""}
                      </p>
                    </button>
                    <div className="flex flex-col items-end gap-3">
                      <span className={`text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 inline-flex items-center gap-1.5 ${st.cls}`}
                        data-testid={`angebotsseite-status-${a.id}`}>
                        <Icon size={11} /> {st.label}
                      </span>
                      <div className="flex gap-2">
                        <button onClick={() => linkKopieren(a)} title="Link kopieren"
                          data-testid={`angebotsseite-link-${a.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:text-[#BA6D52] p-2">
                          <Link2 size={13} />
                        </button>
                        <a href={a.whatsapp_url} target="_blank" rel="noreferrer" title="Per WhatsApp senden"
                          data-testid={`angebotsseite-wa-${a.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:text-emerald-700 p-2">
                          <MessageCircle size={13} />
                        </a>
                        <button onClick={() => mailen(a)} title="Per E-Mail senden"
                          data-testid={`angebotsseite-mail-${a.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:text-[#BA6D52] p-2">
                          <Mail size={13} />
                        </button>
                        <a href={`${API}/admin/angebotsseiten/${a.id}/pdf?pwd=${encodeURIComponent(pwd)}`}
                          target="_blank" rel="noreferrer" title="PDF"
                          data-testid={`angebotsseite-pdf-${a.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:text-[#BA6D52] p-2">
                          <FileText size={13} />
                        </a>
                        <button onClick={() => loeschen(a)} title="Löschen"
                          data-testid={`angebotsseite-del-${a.id}`}
                          className="border border-[#E3DECE] text-[#757067] hover:text-red-700 p-2">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                  {a.status === "angenommen" && (
                    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap border-t border-[#F0EBE0] pt-3">
                      <p className="text-xs text-[#4F6B3E]">
                        Angenommen von {a.angenommen_von}
                        {a.angenommen_funktion ? `, ${a.angenommen_funktion}` : ""}
                        {a.angenommen_am ? ` · ${new Date(a.angenommen_am).toLocaleString("de-DE")}` : ""}
                      </p>
                      {(a.art || "menue") === "stunden" ? (
                        <span className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] bg-[#FAFAF7] px-2.5 py-1 inline-flex items-center gap-1.5"
                          data-testid={`angebotsseite-stunden-badge-${a.id}`}>
                          <Receipt size={11} /> Abrechnung über Stundenkonto
                        </span>
                      ) : a.rechnung?.nummer ? (
                        <span className="text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 inline-flex items-center gap-1.5 border-[#E3DECE] text-[#757067] bg-[#FAFAF7]"
                          data-testid={`angebotsseite-rechnung-status-${a.id}`}>
                          <Receipt size={11} /> Rechnung {a.rechnung.nummer}
                          {a.rechnung.bezahlt_am ? " · bezahlt" : " · offen"}
                        </span>
                      ) : (
                        <button onClick={() => setAktiv(a)}
                          data-testid={`angebotsseite-abrechnen-${a.id}`}
                          className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2 text-[10px] uppercase tracking-[0.15em] inline-flex items-center gap-2">
                          <Receipt size={12} /> Einsatz abgeschlossen — Rechnung
                        </button>
                      )}
                    </div>
                  )}
                  {a.status === "rueckmeldung" && (
                    <div className="mt-3 border-t border-[#F0EBE0] pt-3 flex items-center justify-between gap-3 flex-wrap"
                      data-testid={`angebotsseite-wunsch-hinweis-${a.id}`}>
                      <p className="text-xs text-[#8A5A1E]">
                        Änderungswünsche von {a.rueckmeldung_von || a.ansprechpartner || "Kunde"}
                        {a.rueckmeldung_am ? ` · ${new Date(a.rueckmeldung_am).toLocaleString("de-DE")}` : ""}
                      </p>
                      <button onClick={() => setAktiv(a)}
                        data-testid={`angebotsseite-wunsch-oeffnen-${a.id}`}
                        className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2 text-[10px] uppercase tracking-[0.15em] inline-flex items-center gap-2">
                        <MessageCircle size={12} /> Wünsche ansehen &amp; anpassen
                      </button>
                    </div>
                  )}
                  {a.status === "abgelehnt" && a.ablehnung_grund && (
                    <p className="text-xs text-[#A6543F] mt-3">Grund: {a.ablehnung_grund}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---------------- Editor
  const istStunden = (aktiv.art || "menue") === "stunden";
  const istTagessatz = (aktiv.art || "menue") === "tagessatz";
  const ohneMenue = istStunden || istTagessatz;
  const s = rechneSummen(aktiv);   // live aus den Eingaben, nicht erst nach dem Speichern
  return (
    <div className="space-y-6" data-testid="angebotsseite-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => { setAktiv(null); load(); }} data-testid="angebotsseite-zurueck"
          className="text-[10px] uppercase tracking-[0.2em] text-[#757067] hover:text-[#BA6D52] inline-flex items-center gap-2">
          <ChevronLeft size={14} /> Alle Angebotsseiten
        </button>
        <div className="flex gap-2">
          <a href={aktiv.url} target="_blank" rel="noreferrer" data-testid="angebotsseite-ansehen"
            className="border border-[#E3DECE] text-[#757067] hover:text-[#BA6D52] px-4 py-2.5 text-xs uppercase tracking-[0.15em]">
            Seite ansehen
          </a>
          <button onClick={speichern} disabled={busy} data-testid="angebotsseite-speichern"
            className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
          </button>
        </div>
      </div>

      <p className="text-xs text-[#757067]">
        {aktiv.nummer} · Link: <span className="text-[#BA6D52]">{aktiv.url}</span>
      </p>

      {aktiv.status === "angenommen" && (istStunden ? (
        <div className="border border-[#E3DECE] bg-[#FAFAF7] p-4" data-testid="angebotsseite-stunden-hinweis">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] inline-flex items-center gap-2">
            <Receipt size={13} /> Abrechnung läuft über das Stundenkonto
          </p>
          <p className="text-sm text-[#757067] mt-2 max-w-2xl">
            Der Stundensatz ist bestätigt. Abgerechnet wird über die bestätigten Stundennachweise:
            Stundenkonto öffnen, Einsatztage anhaken, „Sammelrechnung" — Rechnungsadresse und
            Stammdaten kommen aus der Kundenkartei.
          </p>
          <a href="/admin?tab=stundenkonto" data-testid="angebotsseite-zum-stundenkonto"
            className="mt-3 inline-flex items-center gap-2 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2 text-[11px] uppercase tracking-[0.15em]">
            Zum Stundenkonto
          </a>
        </div>
      ) : (
        <RechnungNachEinsatz angebot={aktiv} pwd={pwd}
          onChange={(d) => { setAktiv(d); load(); }} />
      ))}

      {aktiv.status === "rueckmeldung" && (
        <div className="border border-[#E6D2A8] bg-[#FBF5E8] p-4" data-testid="angebotsseite-rueckmeldung">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A5A1E] inline-flex items-center gap-2">
            <MessageCircle size={13} /> Änderungswünsche des Kunden
          </p>
          <p className="text-xs text-[#8A5A1E] mt-2">
            {aktiv.rueckmeldung_von || aktiv.ansprechpartner || "Kunde"}
            {aktiv.rueckmeldung_am ? ` · ${new Date(aktiv.rueckmeldung_am).toLocaleString("de-DE")}` : ""}
          </p>
          {aktiv.rueckmeldung_allgemein && (
            <p className="text-sm text-[#2C2A26] mt-3 leading-relaxed" data-testid="angebotsseite-rueckmeldung-allgemein">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#8A5A1E]">Allgemein:</span>{" "}
              „{aktiv.rueckmeldung_allgemein}"
            </p>
          )}
          <p className="text-sm text-[#757067] mt-3 max-w-2xl">
            Die Wünsche stehen unten direkt beim jeweiligen Gang. Texte anpassen, speichern und das
            überarbeitete Angebot erneut senden — die Wünsche werden dann abgehakt, die Nummer bleibt.
          </p>
          <button onClick={() => mailen(aktiv)} disabled={busy}
            data-testid="angebotsseite-ueberarbeitet-senden"
            className="mt-3 inline-flex items-center gap-2 bg-[#BA6D52] text-white hover:bg-[#A55C43] disabled:opacity-60 px-4 py-2.5 text-[11px] uppercase tracking-[0.15em]">
            <Mail size={13} /> Überarbeitetes Angebot senden
          </button>
          {(aktiv.rueckmeldungen || []).length > 1 && (
            <details className="mt-4" data-testid="angebotsseite-rueckmeldung-verlauf">
              <summary className="text-[10px] uppercase tracking-[0.15em] text-[#8A5A1E] cursor-pointer">
                Verlauf ({aktiv.rueckmeldungen.length} Rückmeldungen)
              </summary>
              <div className="mt-3 space-y-3">
                {aktiv.rueckmeldungen.slice().reverse().map((e, i) => (
                  <div key={i} className="border-l-2 border-[#E6D2A8] pl-3">
                    <p className="text-[11px] text-[#8A5A1E]">
                      {e.von || "Kunde"}{e.am ? ` · ${new Date(e.am).toLocaleString("de-DE")}` : ""}
                    </p>
                    {(e.punkte || []).map((p, x) => (
                      <p key={x} className="text-xs text-[#2C2A26] mt-1">
                        {p.label ? `${p.label}: ` : ""}{p.titel} — „{p.notiz}"
                      </p>
                    ))}
                    {e.allgemein && <p className="text-xs text-[#2C2A26] mt-1">Allgemein: „{e.allgemein}"</p>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FELDER_BASIS.map(([key, label, span, type]) => (
          <div key={key} className={span}>
            <label className={LB}>{label}</label>
            <input type={type || "text"} value={aktiv[key] ?? ""} className={IN}
              data-testid={`angebotsseite-feld-${key}`}
              onChange={(e) => setFeld({ [key]: type === "number" ? Number(e.target.value) : e.target.value })} />
          </div>
        ))}
        <div className="md:col-span-2">
          <label className={LB}>Kundentyp (bestimmt die MwSt)</label>
          <div className="flex gap-2 mt-2">
            {[["firma", "Firma · 19 % MwSt"], ["privat", "Privatkunde · 7 % MwSt"]].map(([wert, label]) => (
              <button key={wert} onClick={() => setFeld({ kundentyp: wert })}
                data-testid={`angebotsseite-kundentyp-${wert}`}
                className={`text-[11px] px-3 py-2 border ${(aktiv.kundentyp || "firma") === wert
                  ? "border-[#BA6D52] text-[#BA6D52] bg-white"
                  : "border-[#E3DECE] text-[#757067] bg-white hover:bg-[#FAF5F1]"}`}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#9A958C] mt-2">
            Privatkunden zahlen 7 % auf Speisen (§ 12 Abs. 2 UStG) und sehen den Preis inklusive
            MwSt. Firmen sehen netto zzgl. 19 %.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className={LB}>Angebotsart</label>
          <div className="flex gap-2 mt-2">
            {[["menue", "Menü · Preis pro Person"], ["stunden", "Mietkoch · Stundensatz"],
              ["tagessatz", "Mietkoch · Tagessatz"]].map(([wert, label]) => (
              <button key={wert} onClick={() => setFeld({ art: wert })}
                data-testid={`angebotsseite-art-${wert}`}
                className={`text-[11px] px-3 py-2 border ${(aktiv.art || "menue") === wert
                  ? "border-[#BA6D52] text-[#BA6D52] bg-white"
                  : "border-[#E3DECE] text-[#757067] bg-white hover:bg-[#FAF5F1]"}`}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#9A958C] mt-2">
            Stundensatz-Angebot: Der Kunde bestätigt deinen Stundensatz, abgerechnet wird über die
            Stundennachweise. Tagessatz-Angebot: fester Preis je Einsatztag inklusive einer
            festgelegten Stundenzahl — alles darüber geht zu deinen Lasten.
          </p>
        </div>

        {ohneMenue && (
          <div className="md:col-span-2 border border-[#E3DECE] bg-[#FAFAF7] p-4">
            <label className={LB}>Einsatztage</label>
            <div className="flex gap-2 mt-2">
              {[["tag", "Ein Tag"], ["zeitraum", "Zeitraum"], ["tage", "Einzelne Tage"]].map(([wert, label]) => (
                <button key={wert} onClick={() => setFeld({ termin_art: wert })}
                  data-testid={`angebotsseite-termin-${wert}`}
                  className={`text-[11px] px-3 py-2 border ${(aktiv.termin_art || "tag") === wert
                    ? "border-[#BA6D52] text-[#BA6D52] bg-white"
                    : "border-[#E3DECE] text-[#757067] bg-white hover:bg-[#FAF5F1]"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className={LB}>{(aktiv.termin_art || "tag") === "zeitraum" ? "Von" : "Einsatztag"}</label>
                <input type="date" value={aktiv.datum || ""} className={IN}
                  data-testid="angebotsseite-feld-datum"
                  onChange={(e) => setFeld({ datum: e.target.value })} />
              </div>
              {(aktiv.termin_art || "tag") === "zeitraum" && (
                <div>
                  <label className={LB}>Bis</label>
                  <input type="date" value={aktiv.datum_bis || ""} className={IN}
                    data-testid="angebotsseite-feld-datum-bis"
                    onChange={(e) => setFeld({ datum_bis: e.target.value })} />
                </div>
              )}
            </div>
            {(aktiv.termin_art || "tag") === "tage" && (
              <div className="mt-4">
                <label className={LB}>Weitere Tage</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(aktiv.tage || []).map((t) => (
                    <span key={t} className="text-xs border border-[#E3DECE] bg-white px-2.5 py-1.5 inline-flex items-center gap-2">
                      {t.split("-").reverse().join(".")}
                      <button onClick={() => setFeld({ tage: (aktiv.tage || []).filter((x) => x !== t) })}
                        className="text-[#A6543F]" data-testid={`angebotsseite-tag-entfernen-${t}`}>×</button>
                    </span>
                  ))}
                </div>
                <input type="date" className={`${IN} mt-2`} data-testid="angebotsseite-tag-hinzu"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    const liste = [...new Set([...(aktiv.tage || []), v])].sort();
                    setFeld({ tage: liste, datum: aktiv.datum || liste[0] });
                    e.target.value = "";
                  }} />
                <p className="text-[11px] text-[#9A958C] mt-2" data-testid="angebotsseite-tage-summe">
                  {(() => {
                    const alle = [...new Set([...(aktiv.tage || []).filter(Boolean),
                      ...(aktiv.datum ? [aktiv.datum] : [])])].sort();
                    return `Insgesamt ${alle.length} Einsatztag${alle.length !== 1 ? "e" : ""}: `
                      + alle.map((t) => t.split("-").reverse().join(".")).join(" · ");
                  })()}
                  {" — mit der Annahme werden alle in deinem Kalender blockiert."}
                </p>
              </div>
            )}
          </div>
        )}

        {(istTagessatz ? FELDER_TAGESSATZ : istStunden ? FELDER_STUNDEN : FELDER_MENUE)
          .map(([key, label, span, type]) => (
          <div key={key} className={span}>
            <label className={LB}>{label}</label>
            <input type={type || "text"} step={type === "number" ? "0.01" : undefined}
              value={aktiv[key] ?? ""} data-testid={`angebotsseite-feld-${key}`}
              onChange={(e) => setFeld({ [key]: e.target.value })} className={IN} />
          </div>
        ))}
        <div className="md:col-span-2">
          <label className={LB}>Begrüßungstext auf der Seite</label>
          <textarea rows="3" value={aktiv.gruss || ""} data-testid="angebotsseite-feld-gruss"
            onChange={(e) => setFeld({ gruss: e.target.value })} className={IN} />
        </div>
      </div>

      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-4" data-testid="angebotsseite-summen">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3">
          Was der Kunde sieht {(aktiv.kundentyp || "firma") === "privat" ? "(Privatkunde, 7 %)" : "(Firma, 19 %)"}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {istTagessatz ? (
            <>
              <div><p className={LB}>Tagessatz netto</p><p className="mt-1">{EUR(s.tagessatz_netto)}</p></div>
              <div>
                <p className={LB}>{s.tage} Einsatztag{s.tage !== 1 ? "e" : ""} · bis {s.stunden_pro_tag} Std.</p>
                <p className="mt-1">{EUR(s.tage_netto)}{s.anfahrt_netto > 0 ? ` + ${EUR(s.anfahrt_netto)} Anfahrt` : ""}</p>
              </div>
              <div><p className={LB}>Summe netto · MwSt {s.mwst_prozent} %</p><p className="mt-1">{EUR(s.netto)} · {EUR(s.mwst_betrag)}</p></div>
              <div><p className={LB}>Gesamt brutto</p><p className="mt-1 font-medium">{EUR(s.brutto)}</p></div>
            </>
          ) : istStunden ? (
            <>
              <div><p className={LB}>Stundensatz netto</p><p className="mt-1">{EUR(s.stundensatz_netto)}</p></div>
              <div><p className={LB}>Stundensatz brutto</p><p className="mt-1">{EUR(s.stundensatz_brutto)}</p></div>
              <div><p className={LB}>Anfahrt netto</p><p className="mt-1">{EUR(s.anfahrt_netto)}</p></div>
              <div><p className={LB}>Abrechnung</p><p className="mt-1">nach Stunden</p></div>
            </>
          ) : (
            <>
              <div><p className={LB}>Pro Person netto</p><p className="mt-1">{EUR(s.pro_person_netto)}</p></div>
              <div>
                <p className={LB}>{s.personen} Personen{s.anfahrt_netto > 0 ? " + Fahrtkosten" : ""}</p>
                <p className="mt-1">
                  {EUR(s.positionen_netto)}
                  {s.anfahrt_netto > 0 ? ` + ${EUR(s.anfahrt_netto)}` : ""}
                </p>
              </div>
              <div><p className={LB}>Summe netto · MwSt {s.mwst_prozent} %</p><p className="mt-1">{EUR(s.netto)} · {EUR(s.mwst_betrag)}</p></div>
              <div><p className={LB}>Gesamt brutto</p><p className="mt-1 font-medium">{EUR(s.brutto)}</p></div>
            </>
          )}
        </div>
        <p className="text-[11px] text-[#9A958C] mt-3">
          Rechnet live mit — „Speichern" übernimmt die Werte auf die Kundenseite.
        </p>
      </div>

      {!ohneMenue && (
      <div className="border border-[#E3DECE] bg-white p-4" data-testid="angebotsseite-gaenge">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Menü / Gänge</p>
          {menuekarten.length > 0 && (
            <select className="border border-[#E3DECE] bg-white px-3 py-2 text-xs max-w-full"
              data-testid="angebotsseite-menue-vorlage" value=""
              onChange={(e) => {
                const m = menuekarten.find((x) => x.id === e.target.value);
                if (!m) return;
                const gaenge = (m.gaenge || []).map((g) => ({
                  label: g.label || "", titel: g.titel || "", beschreibung: g.beschreibung || "",
                }));
                if (!gaenge.length) { toast.error("In diesem Menü sind keine Gänge hinterlegt"); return; }
                if ((aktiv.gaenge || []).some((g) => g.titel || g.beschreibung)
                    && !window.confirm("Die bisherigen Gänge werden ersetzt. Fortfahren?")) return;
                setFeld({ gaenge, untertitel: aktiv.untertitel || m.untertitel || "" });
                toast.success(`Menü „${m.name}" übernommen`);
                e.target.value = "";
              }}>
              <option value="">Fertiges Menü übernehmen …</option>
              {menuekarten.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.gaenge?.length ? ` (${m.gaenge.length} Gänge)` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="space-y-3">
          {(aktiv.gaenge || []).map((g, i) => (
            <div key={i} className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                <input value={g.label || ""} placeholder="Vorspeise" className={`${IN} md:col-span-3`}
                  data-testid={`angebotsseite-gang-label-${i}`}
                  onChange={(e) => setGang(i, { label: e.target.value })} />
                <input value={g.titel || ""} placeholder="Gericht" className={`${IN} md:col-span-4`}
                  data-testid={`angebotsseite-gang-titel-${i}`}
                  onChange={(e) => setGang(i, { titel: e.target.value })} />
                <input value={g.beschreibung || ""} placeholder="Beschreibung (optional)"
                  className={`${IN} md:col-span-4`} data-testid={`angebotsseite-gang-text-${i}`}
                  onChange={(e) => setGang(i, { beschreibung: e.target.value })} />
                <button onClick={() => setFeld({ gaenge: aktiv.gaenge.filter((_, x) => x !== i) })}
                  data-testid={`angebotsseite-gang-del-${i}`}
                  className="border border-[#E3DECE] text-[#757067] hover:text-red-700 p-2.5 mt-1">
                  <Trash2 size={13} />
                </button>
              </div>
              {g.kunden_notiz && (
                <div className="border-l-2 border-[#BA6D52] bg-[#FBF5E8] px-3 py-2 flex items-start justify-between gap-3 flex-wrap"
                  data-testid={`angebotsseite-gang-wunsch-${i}`}>
                  <p className="text-xs text-[#8A5A1E] leading-relaxed">
                    <span className="uppercase tracking-[0.15em] text-[10px]">Kundenwunsch:</span>{" "}
                    „{g.kunden_notiz}"
                  </p>
                  <button onClick={() => setGang(i, { kunden_notiz: "" })}
                    data-testid={`angebotsseite-gang-wunsch-erledigt-${i}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E6D2A8] text-[#8A5A1E] hover:bg-[#F6ECD8] px-2.5 py-1.5 shrink-0">
                    Erledigt
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <button onClick={() => setFeld({ gaenge: [...(aktiv.gaenge || []), { label: "", titel: "", beschreibung: "" }] })}
          data-testid="angebotsseite-gang-add"
          className="mt-3 text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-4 py-2 inline-flex items-center gap-2">
          <Plus size={13} /> Gang
        </button>
      </div>
      )}

      <div className="border border-[#E3DECE] bg-white p-4" data-testid="angebotsseite-leistungen">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Enthaltene Leistungen</p>
          {vorlagen.vorlagen.length > 0 && (
            <select className="border border-[#E3DECE] bg-white px-3 py-2 text-xs max-w-full"
              data-testid="angebotsseite-leistungen-vorlage" value=""
              onChange={(e) => {
                const v = vorlagen.vorlagen[Number(e.target.value)];
                if (!v) return;
                const vorhanden = (aktiv.leistungen || []).filter(Boolean);
                setFeld({ leistungen: [...new Set([...vorhanden, ...v.leistungen])] });
                toast.success("Leistungen übernommen");
                e.target.value = "";
              }}>
              <option value="">Leistungen aus früherem Angebot …</option>
              {vorlagen.vorlagen.map((v, i) => (
                <option key={i} value={i}>{v.titel} ({v.leistungen.length})</option>
              ))}
            </select>
          )}
        </div>
        <div className="space-y-2">
          {(aktiv.leistungen || []).map((l, i) => (
            <div key={i} className="flex gap-2">
              <input value={l} className={`${IN} flex-1`} data-testid={`angebotsseite-leistung-${i}`}
                list="leistungen-vorschlaege"
                onChange={(e) => setFeld({
                  leistungen: aktiv.leistungen.map((x, y) => (y === i ? e.target.value : x)),
                })} />
              <button onClick={() => setFeld({ leistungen: aktiv.leistungen.filter((_, x) => x !== i) })}
                data-testid={`angebotsseite-leistung-del-${i}`}
                className="border border-[#E3DECE] text-[#757067] hover:text-red-700 p-2.5 mt-1">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <button onClick={() => setFeld({ leistungen: [...(aktiv.leistungen || []), ""] })}
          data-testid="angebotsseite-leistung-add"
          className="mt-3 text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-4 py-2 inline-flex items-center gap-2">
          <Plus size={13} /> Zeile
        </button>
        <datalist id="leistungen-vorschlaege">
          {vorlagen.zeilen.map((z) => <option key={z} value={z} />)}
        </datalist>
      </div>

      <div>
        <label className={LB}>Rahmenbedingungen</label>
        <textarea rows="5" value={aktiv.bedingungen || ""} data-testid="angebotsseite-bedingungen"
          onChange={(e) => setFeld({ bedingungen: e.target.value })} className={IN} />
      </div>

      <button onClick={speichern} disabled={busy} data-testid="angebotsseite-speichern-unten"
        className="w-full bg-[#2C2A26] text-white hover:bg-[#1F1D1A] px-5 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2">
        <Save size={15} /> Angebotsseite speichern
      </button>
    </div>
  );
}
